import { Provider } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import type { CaseTypeEditorContext } from '../../../effects/case-type-editor.effects';
import type { MethodTypeManagerContext } from '../../../state/assigned-method-types.state';
import { AssignedMethodTypesState } from '../../../state/assigned-method-types.state';
import { StagedCaseDataState } from '../../../state/staged-case-data.state';
import { StagedMethodTypesState } from '../../../state/staged-method-types.state';
import { methodPipelineManagerProviders } from '../../pipelines/method-pipeline-manager/method-pipeline-manager.facade.providers';
import { pipelineEditorCascadeProviders } from '../../pipelines/pipeline-editor/pipeline-editor.facade.providers';
import { resourceManagerProviders } from '../../resources/resource-manager/resource-manager.facade.providers';
import { METHOD_TYPE_MANAGER_CONTEXT } from '../../method-types/method-type-manager/method-type-manager.facade';
import { methodTypeManagerProviders } from '../../method-types/method-type-manager/method-type-manager.facade.providers';
import { methodTypeEditorProviders } from '../../method-types/method-type-editor/method-type-editor.facade.providers';

import { CASE_TYPE_EDITOR_CONTEXT } from './case-type-editor-page.facade';
import { caseTypeEditorProviders } from './case-type-editor.facade.providers';

/**
 * The full DI scope of the case-type editor page — the outermost consumer that
 * composes the case-type editor tier, the case-data ledger, the method-type
 * MANAGER, and (for the per-open method-type editor DIALOG it opens) the entire
 * method-type editor session scope + cascade. The page is the outermost consumer
 * of every shared session ledger, so all of them resolve here.
 *
 * Relationship to `methodTypeEditorPageProviders`: tiers (5)+(6) are the SAME
 * composition that page uses for the method-type editor, with ONE deliberate
 * difference — the case-type page does NOT provide the per-open context tokens
 * (`METHOD_TYPE_EDITOR_CONTEXT`, `METHOD_PIPELINE_MANAGER_CONTEXT`,
 * `RESOURCE_MANAGER_CONTAINER_ID`). Here the method-type editor is embedded via
 * `MethodTypeEditorDialogComponent`, which provides those three (and the two
 * embedded manager facades) PER OPEN from `MAT_DIALOG_DATA`. The manager facades
 * carried in by (5)'s bundles are never instantiated at page scope (no
 * pipeline/resource manager renders here — they live inside the dialog), so they
 * are inert; the dialog re-provides them scoped to the method type each open
 * targets.
 *
 * Served live by the `caseTypes/edit/new` and `caseTypes/edit/:caseTypeId`
 * routes (telespot-protocols-feature). See the method-type-manager story for
 * the canonical shared-tier-vs-per-open recipe.
 */
export const caseTypeEditorPageProviders: Provider[] = [
  // (1) Session identity — the route's case-type id for an edit, or `undefined`
  // (create). Unlike the method-type id, it is not remapped into child contexts.
  {
    provide: CASE_TYPE_EDITOR_CONTEXT,
    useFactory: (route: ActivatedRoute): CaseTypeEditorContext => {
      const routeId = route.snapshot.paramMap.get('caseTypeId'); // null on the create route
      return routeId !== null ? { caseTypeId: routeId, persisted: true } : { caseTypeId: undefined, persisted: false };
    },
    deps: [ActivatedRoute],
  },

  // (2) Case-type editor tier (state, retrieve query over the session repos,
  // effects, facade).
  ...caseTypeEditorProviders,

  // (3) Case-data staging ledger — owned by the page so the staged fields
  // survive the manager unmount and the field-editor dialog stack.
  StagedCaseDataState,

  // (4) Method-type MANAGER tier (left catalog + manager facade) + the shared
  // method-type staging ledgers + the manager context (derived from the session
  // identity; the assigned ledger is seeded by the editor load).
  ...methodTypeManagerProviders(),
  StagedMethodTypesState,
  AssignedMethodTypesState,
  {
    provide: METHOD_TYPE_MANAGER_CONTEXT,
    useFactory: (context: CaseTypeEditorContext): MethodTypeManagerContext => ({
      caseTypeId: context.caseTypeId,
      persisted: context.persisted,
    }),
    deps: [CASE_TYPE_EDITOR_CONTEXT],
  },

  // (5) The method-type editor SESSION scope the per-open dialog resolves
  // against (editor trio + asset catalog, embedded pipeline + resource manager
  // shared tiers). The per-open context tokens are deliberately omitted — the
  // dialog supplies them.
  ...methodTypeEditorProviders,
  ...methodPipelineManagerProviders,
  ...resourceManagerProviders,

  // (6) Page cascade + label/staging ledgers — also supplies the shared resource
  // + pipeline staging ledgers and the SaveProtocolCascade tier (so the page
  // facade's `SaveProtocolCascadeEffects` resolves and `consolidateCaseType`
  // can run).
  ...pipelineEditorCascadeProviders,
];
